import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, XIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { WaitlistCTA } from '../components/WaitlistCTA';
const GIGA_LOGO = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/CleanShot_2026-06-14_at_23.17.432x.png";
type FeatureRow = {
  label: string;
  giga: string;
  chatgpt: string;
};
const FEATURES: FeatureRow[] = [{
  label: 'Connects to your business tools',
  giga: '3,200+ integrations via managed OAuth',
  chatgpt: 'Limited plugins, no private OAuth'
}, {
  label: 'Takes real actions',
  giga: 'Reads & writes live data across tools',
  chatgpt: 'Suggests, you execute manually'
}, {
  label: 'Lives in Slack',
  giga: 'A teammate you @mention',
  chatgpt: 'A separate browser tab'
}, {
  label: 'Shared team memory',
  giga: 'One AI employee, shared context',
  chatgpt: 'Per-user, starts from scratch'
}, {
  label: 'Professional deliverables',
  giga: 'PDFs, Excel, decks, web apps',
  chatgpt: 'Text and basic files'
}, {
  label: 'Scheduled automation',
  giga: 'Recurring tasks without prompting',
  chatgpt: 'Manual, one-off prompts'
}];
const GIGA_REASONS = ['You need an AI that connects to your business tools and takes real actions', 'You want your team to share one AI employee with shared context and memory', 'You need professional deliverables: PDFs, Excel reports, PowerPoint decks, web applications', 'You want automated, scheduled tasks running without manual prompting', "You want an AI that proactively suggests automations based on your team's work patterns", 'You need ad campaign management, cross-tool analytics, or codebase contributions'];
const CHATGPT_REASONS = ['You need a general-purpose AI for brainstorming, writing, and one-off questions', "You work primarily solo and don't need team-wide AI capabilities", "You don't need your AI connected to business tools like Stripe, HubSpot, or ad platforms", 'You want a free or low-cost option for personal use'];
const INTEGRATIONS = [{
  name: 'Stripe',
  slug: 'stripe'
}, {
  name: 'HubSpot',
  slug: 'hubspot'
}, {
  name: 'Meta',
  slug: 'meta'
}, {
  name: 'Google Ads',
  slug: 'googleads'
}, {
  name: 'Notion',
  slug: 'notion'
}, {
  name: 'Linear',
  slug: 'linear'
}, {
  name: 'GitHub',
  slug: 'github'
}, {
  name: 'Slack',
  slug: 'slack'
}, {
  name: 'Salesforce',
  slug: 'salesforce'
}, {
  name: 'Asana',
  slug: 'asana'
}, {
  name: 'Trello',
  slug: 'trello'
}, {
  name: 'Mailchimp',
  slug: 'mailchimp'
}, {
  name: 'Dropbox',
  slug: 'dropbox'
}, {
  name: 'Zapier',
  slug: 'zapier'
}, {
  name: 'Airtable',
  slug: 'airtable'
}, {
  name: 'Google Drive',
  slug: 'googledrive'
}];
const FAQS = [{
  q: 'Can Giga replace ChatGPT entirely?',
  a: 'Giga and ChatGPT serve different purposes. ChatGPT is a general-purpose AI chatbot for conversations, writing, and brainstorming. Giga is an AI employee built for business operations: it connects to your tools, takes real actions, delivers professional documents, and automates recurring work. Many teams use both.'
}, {
  q: 'Is Giga free?',
  a: 'We offer a 7-day free trial. After that, plans start at $25/month for the Starter plan, with Pro and Custom tiers available for higher usage needs.'
}, {
  q: 'Does Giga use ChatGPT or OpenAI models?',
  a: "Giga is model-agnostic. We route tasks to the best frontier model for the job, including OpenAI's GPT-4o, Anthropic's Claude 3.5 Sonnet, and Google's Gemini 1.5 Pro. You get the intelligence of these models combined with Giga's tool execution engine."
}, {
  q: "What can Giga do that ChatGPT can't?",
  a: "Giga can authenticate into your company's specific tools (like Stripe, Salesforce, or AWS) using OAuth, read live private data, take actions (like updating a record or deploying code), and format the output into a downloadable PDF or Excel file—all without leaving Slack."
}];
export function CompareChatGPT() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="w-full overflow-x-hidden bg-[#FAF8F4] text-[#2B211A] font-body" data-id="element-6545" data-mp="2lb76jept59ih">
      {/* ===== HERO ===== */}
      <section className="relative w-full overflow-hidden md:overflow-visible bg-[#1C1510] pt-28 md:pt-40 pb-16 md:pb-64" data-id="element-6546" data-mp="3003cn68b0cgg">
        <div className="absolute inset-0 dot-matrix-dark opacity-70 pointer-events-none" data-id="element-6547" data-mp="2v67hscnc75pi" />
        <motion.div aria-hidden="true" className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.15] blur-[120px]" animate={{
        opacity: [0.5, 0.8, 0.5],
        scale: [0.9, 1.05, 0.9]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-6548" data-mp="391jlh9jh06ik" />
        <div className="relative z-10 max-w-[1000px] mx-auto px-6 md:px-10 text-center" data-id="element-6549" data-mp="1okepoev1vdfn">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-mono text-xs tracking-widest uppercase text-[#F97316] mb-6 block" data-id="element-6550" data-mp="1jlk81g8tjux4">
            Giga vs ChatGPT
          </motion.span>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="font-title font-bold text-4xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.05] mb-6" data-id="element-6551" data-mp="3likoq2c6m1eg">
            The AI That Does the Work
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-lg md:text-xl text-neutral-400 leading-relaxed max-w-3xl mx-auto mb-4" data-id="element-6552" data-mp="3ggvbcg1hlbn7">
            ChatGPT answers your questions. Giga connects to your tools, runs
            your reports, and delivers real outputs. One is a chatbot. The other
            is an AI employee.
          </motion.p>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-sm text-neutral-500 mb-10" data-id="element-6553" data-mp="14bcaeqf0ybf2">
            Last updated: June 2026
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6554" data-mp="pd0ewn3e18ph">
            <WaitlistCTA className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg shadow-[#EA580C]/20" data-id="element-6555" data-mp="1i38zsmpb677r">
              Get Started for Free
            </WaitlistCTA>
            <Link to="/demo" className="w-full sm:w-auto bg-white/5 hover:bg-white/10 text-white border border-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors inline-block" data-id="element-6556" data-mp="25r0axmz44e7g">
              Watch Giga Work
            </Link>
          </motion.div>
        </div>

        {/* Intro Cards — flow inline on mobile, overlap the hero on desktop */}
        <div className="relative mt-12 px-6 md:absolute md:left-0 md:right-0 md:-bottom-40 md:mt-0 md:px-10 z-20" data-id="element-6557" data-mp="2dc2tp39m9b87">
          <div className="max-w-[1280px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-6558" data-mp="2n85uo5sqf3tk">
            {/* Giga Card */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.6
          }} className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl shadow-black/10 border border-neutral-200 relative overflow-hidden group" data-id="element-6559" data-mp="2s6h4igj11ebs">
              <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-6560" data-mp="3ay07wogyquec">
                <div className="w-12 h-12 rounded-xl bg-[#2B211A] flex items-center justify-center shadow-md overflow-hidden" data-id="element-6561" data-mp="3azuqvx8occfs">
                  <img src={GIGA_LOGO} alt="Giga" className="w-8 h-8 object-contain" data-id="element-6562" data-mp="52nhi18dndrn" />
                </div>
                <span className="font-title font-bold text-xl" data-id="element-6563" data-mp="3ufb4yjj96s">Giga</span>
              </div>
              <p className="text-neutral-600 leading-relaxed" data-id="element-6564" data-mp="e3d90sqluhzk">
                An AI employee that lives in Slack, connects to all your
                business tools, takes real actions, and delivers finished work —
                not just answers.
              </p>
            </motion.div>

            {/* ChatGPT Card */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl shadow-black/10 border border-neutral-200" data-id="element-6565" data-mp="3dj9zb1iwemjg">
              <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-6566" data-mp="2yrzwkwq2pzbt">
                <div className="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center p-2.5" data-id="element-6567" data-mp="2tv9habm3f7rg">
                  <img src="https://cdn.simpleicons.org/openai/171717" alt="ChatGPT" className="w-full h-full object-contain" data-id="element-6568" data-mp="19bnmjx0wv5o3" />
                </div>
                <span className="font-title font-bold text-xl" data-id="element-6569" data-mp="10br4ie911520">ChatGPT</span>
              </div>
              <p className="text-neutral-600 leading-relaxed" data-id="element-6570" data-mp="109xwauirmcrw">
                A general-purpose chatbot for conversations, writing, and
                brainstorming. Powerful for ideas — but it can't take action
                across your stack.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== COMPARISON TABLE ===== */}
      <section className="w-full pt-16 md:pt-64 pb-20 md:pb-28 px-6 md:px-10" data-id="element-6571" data-mp="1j20j6nm02pal">
        <div className="max-w-[1100px] mx-auto" data-id="element-6572" data-mp="1o01x6u4kjyf7">
          <div className="text-center mb-10 md:mb-16" data-id="element-6573" data-mp="7n3gwc0t23la">
            <span className="font-mono text-xs tracking-widest uppercase text-[#EA580C] mb-4 block" data-id="element-6574" data-mp="7likshyueclb">
              Side by side
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight" data-id="element-6575" data-mp="2t0sbum0w1epq">
              How Giga compares to ChatGPT
            </h2>
          </div>

          <div className="overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm" data-id="element-6576" data-mp="2xytrewz3uvwg">
            {/* Desktop column header (hidden on mobile) */}
            <div className="hidden md:grid grid-cols-3 bg-[#1C1510] text-white" data-id="element-6577" data-mp="2o8s4w9mehljn">
              <div className="p-5 md:p-6 font-mono text-xs tracking-widest uppercase text-neutral-400" data-id="element-6578" data-mp="2a9j2ccr4apy5">
                Capability
              </div>
              <div className="p-5 md:p-6 font-title font-bold text-base md:text-lg text-[#F97316]" data-id="element-6579" data-mp="2ecyxt5rgak99">
                Giga
              </div>
              <div className="p-5 md:p-6 font-title font-bold text-base md:text-lg text-neutral-300" data-id="element-6580" data-mp="25apk670vyarn">
                ChatGPT
              </div>
            </div>
            {FEATURES.map((row, i) => <div key={row.label} className={`md:grid md:grid-cols-3 border-t border-neutral-200 ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F4]'}`} data-id="element-6581" data-mp="3jktpeaovg8w" data-mpi={i}>
                {/* Capability label — full-width header on mobile */}
                <div className="px-5 pt-5 pb-2 md:p-6 font-bold md:font-medium text-base md:text-base" data-id="element-6582" data-mp="1j329w8eiwxoo">
                  {row.label}
                </div>
                {/* Giga */}
                <div className="px-5 py-2 md:p-6 text-sm md:text-base text-neutral-700 md:border-l border-neutral-200 flex items-start gap-2" data-id="element-6583" data-mp="1t10031fy14jx">
                  <CheckIcon className="w-5 h-5 text-[#EA580C] flex-shrink-0 mt-0.5" data-id="element-6584" data-mp="26qtltaw5j8pf" />
                  <span data-id="element-6585" data-mp="21s0anp4c7tsf">
                    <span className="md:hidden font-bold text-[#EA580C] mr-1" data-id="element-6586" data-mp="21ss68zxxprip">
                      Giga:
                    </span>
                    {row.giga}
                  </span>
                </div>
                {/* ChatGPT */}
                <div className="px-5 pt-2 pb-5 md:p-6 text-sm md:text-base text-neutral-500 md:border-l border-neutral-200 flex items-start gap-2" data-id="element-6587" data-mp="mc01hgj2ome1">
                  <XIcon className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" data-id="element-6588" data-mp="herkhj9nuoed" />
                  <span data-id="element-6589" data-mp="172nilhktl5y2">
                    <span className="md:hidden font-bold text-neutral-500 mr-1" data-id="element-6590" data-mp="1735eue7hfk5z">
                      ChatGPT:
                    </span>
                    {row.chatgpt}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ===== SLACK NARRATIVE ===== */}
      <section className="w-full py-14 md:py-28 px-6 md:px-10" data-id="element-6591" data-mp="1q06jfxx4ji9e">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-6592" data-mp="1pzomt06jv20y">
          {/* Slack Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="w-full min-h-[280px] md:h-[420px] rounded-2xl overflow-hidden shadow-2xl border border-neutral-200 flex flex-col md:flex-row bg-white font-body" data-id="element-6593" data-mp="1ysrz355gl8u0">
            {/* Slack Sidebar (hidden on mobile) */}
            <div className="hidden md:flex w-56 bg-[#3F0E40] text-white/80 flex-col py-4 shrink-0" data-id="element-6594" data-mp="20clx7iedcht">
              <div className="font-bold text-white px-4 mb-6 flex items-center justify-between" data-id="element-6595" data-mp="3tb352to17x4n">
                Acme Corp
                <ChevronDownIcon className="w-4 h-4 opacity-70" data-id="element-6596" data-mp="3jasrh7y34noy" />
              </div>
              <div className="px-4 mb-1 text-sm opacity-70" data-id="element-6597" data-mp="ksx8k77i6ld6">Channels</div>
              <div className="flex flex-col mb-6" data-id="element-6598" data-mp="ks59unim07io">
                <div className="px-4 py-1 text-sm hover:bg-white/10 cursor-pointer" data-id="element-6599" data-mp="ftamr9dlzwsr">
                  # general
                </div>
                <div className="px-4 py-1 text-sm bg-[#1164A3] text-white font-medium" data-id="element-6600" data-mp="b1j3zl0p2vd6">
                  # growth
                </div>
                <div className="px-4 py-1 text-sm hover:bg-white/10 cursor-pointer" data-id="element-6601" data-mp="13jelb2bxx6cw">
                  # marketing
                </div>
              </div>
              <div className="px-4 mb-1 text-sm opacity-70" data-id="element-6602" data-mp="yr50u1tlgyi0">
                Direct messages
              </div>
              <div className="flex flex-col" data-id="element-6603" data-mp="j9dhrzs4xhtx">
                <div className="px-4 py-1 text-sm hover:bg-white/10 cursor-pointer flex items-center gap-2" data-id="element-6604" data-mp="oaaxm5po9zs9">
                  <div className="w-4 h-4 rounded bg-white/20 flex items-center justify-center" data-id="element-6605" data-mp="9dag0v173l7i">
                    <img src={GIGA_LOGO} alt="" className="w-3 h-3 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6606" data-mp="easflutboo2u" />
                  </div>
                  Giga
                </div>
              </div>
            </div>

            {/* Slack Main Area */}
            <div className="flex-1 flex flex-col bg-white overflow-hidden" data-id="element-6607" data-mp="1723qm04qrkzz">
              {/* Channel Header */}
              <div className="border-b border-neutral-200 px-4 py-3 flex items-center gap-2 font-bold text-[#1D1C1D]" data-id="element-6608" data-mp="xag48dn1ku5d">
                # growth
              </div>

              {/* Messages */}
              <div className="flex-1 p-4 md:p-6 flex flex-col gap-6 overflow-y-auto" data-id="element-6609" data-mp="1pqqsk9rmfu9c">
                {/* User Message */}
                <div className="flex items-start gap-3" data-id="element-6610" data-mp="3agmkhc20qe4d">
                  <div className="w-9 h-9 rounded bg-neutral-200 flex-shrink-0" data-id="element-6611" data-mp="3af2z571i9cvq" />
                  <div data-id="element-6612" data-mp="35ht2b6bsdyxv">
                    <p className="text-[#1D1C1D] text-[15px] font-bold mb-0.5 leading-tight" data-id="element-6613" data-mp="35g8hi8f9xud7">
                      Tom{' '}
                      <span className="text-neutral-500 font-normal text-xs ml-1" data-id="element-6614" data-mp="3f7cn4hj282vd">
                        9:02 AM
                      </span>
                    </p>
                    <p className="text-[#1D1C1D] text-[15px] leading-relaxed" data-id="element-6615" data-mp="2rk4twxywok9p">
                      <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded" data-id="element-6616" data-mp="2ml1npa76w805">
                        @Giga
                      </span>{' '}
                      audit our Meta Ads and Google Ads spend. Compare vs last
                      month.
                    </p>
                  </div>
                </div>

                {/* Giga Message */}
                <div className="flex items-start gap-3" data-id="element-6617" data-mp="brxd3gaolta8">
                  <div className="w-9 h-9 rounded bg-[#1C1510] border border-neutral-200 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-6618" data-mp="bti8f84tlg95">
                    <img src={GIGA_LOGO} alt="Giga" className="w-6 h-6 object-contain" data-id="element-6619" data-mp="39jfnyvgc2mv6" />
                  </div>
                  <div data-id="element-6620" data-mp="3ekbrv90oq90l">
                    <p className="text-[#1D1C1D] text-[15px] font-bold mb-0.5 leading-tight flex items-center gap-1.5" data-id="element-6621" data-mp="3ehpnfi93c9ml">
                      Giga
                      <span className="text-[#F97316] text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-[#F97316]/10 leading-none" data-id="element-6622" data-mp="2lvxumvhf7gq3">
                        APP
                      </span>
                      <span className="text-neutral-500 font-normal text-xs ml-1" data-id="element-6623" data-mp="2uvktpamogafz">
                        9:02 AM
                      </span>
                    </p>
                    <div className="text-[#1D1C1D] text-[15px] leading-relaxed" data-id="element-6624" data-mp="234n3cd7s91xo">
                      ✅ Done. Pulled data from both platforms. Key findings:
                      <br data-id="element-6625" data-mp="274p6yrx2w5fl" />
                      Meta Ads: CPA down 12% MoM, ROAS up to 3.4x
                      <br data-id="element-6626" data-mp="1zlfz53480esn" />
                      Google Ads: Brand campaigns strong, pMax underperforming
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <div data-id="element-6627" data-mp="1ln83qzmu1ljl">
            <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight mb-6" data-id="element-6628" data-mp="1lnrcpqui8zlh">
              A teammate in Slack, not a tab in your browser.
            </h2>
            <div className="space-y-5 text-neutral-600 leading-relaxed" data-id="element-6629" data-mp="1bx526b1naz9n">
              <p data-id="element-6630" data-mp="1bvtw24kq9ffd">
                Giga lives where your team already works. You @mention it in a
                channel or DM it directly, just like messaging a colleague. It
                sees the conversation context, knows who's asking, and remembers
                what you discussed last week.
              </p>
              <p data-id="element-6631" data-mp="syan10y9rwsy">
                ChatGPT lives in a separate browser tab. Every conversation
                starts from scratch. You provide the context manually every
                time, and whatever ChatGPT produces, you still have to bring it
                back to your team in Slack.
              </p>
              <p data-id="element-6632" data-mp="27ukbe86febgk">
                Giga's integration into Slack means your entire team benefits.
                When Giga automates a weekly report, everyone in the channel
                sees it. There's no copy-pasting between apps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== INTEGRATIONS ===== */}
      <section className="w-full py-14 md:py-28 px-6 md:px-10 bg-white" data-id="element-6633" data-mp="22wsc2t3gbn2c">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-6634" data-mp="1p4u5aox7q39c">
          <div className="order-2 lg:order-1" data-id="element-6635" data-mp="1t67en8zikol0">
            <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight mb-6" data-id="element-6636" data-mp="1f5mt4527sent">
              Connected to the tools
              <br data-id="element-6637" data-mp="1k4zhwvcnbvt1" />
              you already use.
            </h2>
            <div className="space-y-5 text-neutral-600 leading-relaxed" data-id="element-6638" data-mp="10ixx4d768jm6">
              <p data-id="element-6639" data-mp="10j679xbwtato">
                Giga connects to your business tools with managed OAuth. One
                click to connect Stripe, HubSpot, Meta Ads, Google Ads, Notion,
                Linear, GitHub, and the rest of your stack. Giga can read and
                write data across all of them in a single request.
              </p>
              <p data-id="element-6640" data-mp="1jhilvyllbcwn">
                "Pull our Stripe revenue this month, compare it to our Meta Ads
                spend, and create a Linear issue if ROAS dropped below 3x."
                That's one message. Three tools. Real actions taken.
              </p>
              <p data-id="element-6641" data-mp="1t6tu7j0nkdjh">
                ChatGPT has limited integrations through plugins and can browse
                the web, but it can't connect to your Stripe account, update
                your CRM, or manage your ad campaigns. You describe what you
                want, ChatGPT suggests what to do, and you go execute it
                yourself across each tool.
              </p>
            </div>
          </div>
          <div className="order-1 lg:order-2 bg-[#1C1510] rounded-3xl p-6 md:p-10" data-id="element-6642" data-mp="2ar92ovx91jly">
            <div className="grid grid-cols-4 gap-2.5 md:gap-4" data-id="element-6643" data-mp="25sfp73hypsls">
              {INTEGRATIONS.map(integration => <div key={integration.slug} className="aspect-square rounded-xl bg-white/5 border border-white/10 flex items-center justify-center p-3 md:p-4 transition-colors hover:bg-white/10" title={integration.name} data-id="element-6644" data-mp="2jo09a29fggus">
                  <img src={`https://cdn.simpleicons.org/${integration.slug}/ffffff`} alt={integration.name} className="w-8 h-8 object-contain opacity-80" data-id="element-6645" data-mp="1mzu1j6h7l8ca" />
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CHOOSER ===== */}
      <section className="w-full py-14 md:py-28 px-6 md:px-10" data-id="element-6646" data-mp="1vyfdujw37r4m">
        <div className="max-w-[1200px] mx-auto" data-id="element-6647" data-mp="189w7wb0ifqfp">
          <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight text-center mb-8 md:mb-16" data-id="element-6648" data-mp="188l3c8z8qenj">
            When to choose Giga vs ChatGPT
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-6649" data-mp="3exn4v5du7k6i">
            {/* Giga column */}
            <div data-id="element-6650" data-mp="3jr7ti6tjj1t1">
              <div className="rounded-2xl bg-[#EA580C] p-6 md:p-8 flex items-center justify-center mb-6" data-id="element-6651" data-mp="3jrrclquxfw8n">
                <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg overflow-hidden" data-id="element-6652" data-mp="3axmd6j1q9chi">
                  <img src={GIGA_LOGO} alt="Giga" className="w-10 h-10 object-contain" data-id="element-6653" data-mp="2h9mj9nzmgwz" />
                </div>
              </div>
              <p className="text-center font-bold text-[#EA580C] mb-6" data-id="element-6654" data-mp="6i3ag31fjvpv">
                Choose Giga if:
              </p>
              <ul className="space-y-3" data-id="element-6655" data-mp="3or3x44ybsr82">
                {GIGA_REASONS.map(reason => <li key={reason} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-neutral-200" data-id="element-6656" data-mp="3tmsz0extik7j">
                    <span className="w-6 h-6 rounded-full bg-[#EA580C] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-6657" data-mp="2d8jbfn1egzhr">
                      <CheckIcon className="w-4 h-4 text-white" data-id="element-6658" data-mp="1y6t8hko07ita" />
                    </span>
                    <span className="text-sm md:text-base text-neutral-700" data-id="element-6659" data-mp="1y8nnkiclggpg">
                      {reason}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* ChatGPT column */}
            <div data-id="element-6660" data-mp="1kfxyu65okxon">
              <div className="rounded-2xl bg-neutral-200 p-6 md:p-8 flex items-center justify-center mb-6" data-id="element-6661" data-mp="1ked5gjv3rksd">
                <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg text-neutral-500 font-title font-bold text-xl" data-id="element-6662" data-mp="1fh4i84v3kqk9">
                  AI
                </div>
              </div>
              <p className="text-center font-bold text-neutral-500 mb-6" data-id="element-6663" data-mp="16fwvmk1y1e60">
                Choose ChatGPT if:
              </p>
              <ul className="space-y-3" data-id="element-6664" data-mp="30to2jbsw1jxr">
                {CHATGPT_REASONS.map(reason => <li key={reason} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-neutral-200" data-id="element-6665" data-mp="30swgosp3f0ko">
                    <span className="w-6 h-6 rounded-full bg-neutral-300 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-6666" data-mp="2vsqj6vw56juo">
                      <CheckIcon className="w-4 h-4 text-white" data-id="element-6667" data-mp="38cf7vm8hq04c" />
                    </span>
                    <span className="text-sm md:text-base text-neutral-700" data-id="element-6668" data-mp="3d3drauorzebt">
                      {reason}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="w-full py-14 md:py-28 px-6 md:px-10" data-id="element-6669" data-mp="2khkwb79q13zl">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16" data-id="element-6670" data-mp="2pf4a662iber0">
          <div data-id="element-6671" data-mp="2ucmm85btma38">
            <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight" data-id="element-6672" data-mp="2uc4eocjpk9g0">
              Questions people ask before adding Giga
            </h2>
          </div>
          <div className="space-y-4" data-id="element-6673" data-mp="9m5cykt9awlu">
            {FAQS.map((faq, i) => {
            const isOpen = openFaq === i;
            return <div key={faq.q} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden" data-id="element-6674" data-mp="dmyyx8n521c3" data-mpi={i}>
                  <button onClick={() => setOpenFaq(isOpen ? null : i)} className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left" aria-expanded={isOpen} data-id="element-6675" data-mp="c9pi3xessq89">
                    <span className={`font-bold text-base md:text-lg ${isOpen ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} data-id="element-6676" data-mp="m7dpbmtlbq7b">
                      {faq.q}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-[#EA580C]' : 'text-neutral-400'}`} data-id="element-6677" data-mp="v5pfjnqjolz8" />
                  </button>
                  {isOpen && <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1" data-id="element-6678" data-mp="14wjz2kcep7gz">
                      <p className="text-neutral-600 leading-relaxed" data-id="element-6679" data-mp="zx7cm0pg62ex">
                        {faq.a}
                      </p>
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* ===== CLOSING CTA ===== */}
      <section className="w-full px-6 md:px-10 pb-20 md:pb-28" data-id="element-6680" data-mp="19x81e2635aqi">
        <div className="max-w-[1280px] mx-auto relative overflow-hidden rounded-3xl md:rounded-[2rem] bg-[#1C1510] px-6 md:px-16 py-12 md:py-24 text-center" data-id="element-6681" data-mp="1sjffki1kf2ra">
          <div className="absolute inset-0 dot-matrix-dark opacity-70 pointer-events-none" data-id="element-6682" data-mp="1xge8wyden5of" />
          <motion.div aria-hidden="true" className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-[#F97316]/[0.18] blur-[120px]" animate={{
          opacity: [0.5, 0.85, 0.5]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-6683" data-mp="1jlbvzra54g24" />
          <div className="relative z-10" data-id="element-6684" data-mp="10tj27un8yqtc">
            <h2 className="font-title font-bold text-3xl md:text-5xl lg:text-6xl text-white tracking-tight mb-6" data-id="element-6685" data-mp="10qeuwoehjeyg">
              Stop asking. Start doing.
            </h2>
            <p className="text-lg text-neutral-400 max-w-2xl mx-auto mb-10" data-id="element-6686" data-mp="qutwf08mkwtg">
              Give your team an AI employee that connects to your tools and gets
              real work done.
            </p>
            <WaitlistCTA className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg shadow-[#EA580C]/20" data-id="element-6687" data-mp="2xk59wgbfm8z4">
              Get Started for Free
              <ArrowRightIcon className="w-5 h-5" data-id="element-6688" data-mp="2jrywv33ksi3w" />
            </WaitlistCTA>
          </div>
        </div>
      </section>
    </div>;
}